import './PlanCard.scss';

import * as Collapsible from '@radix-ui/react-collapsible';
import {
  Body,
  ButtonV2,
  noop,
  ProductOffering,
  resolveProductOfferingType,
} from '@travelwin/core';
import classNames from 'classnames';
import { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PlanOperatorFooter from '@/components/PlanOperatorFooter';
import { isStockAvailable } from '@/utils';

import PlanTitle from '../PlanTitle';
import BuyButton from './components/BuyButton';
import FeaturedLabel from './components/FeaturedLabel';
import { PlanBenefits } from './components/PlanBenefits';

export enum PlanCardPageType {
  PaymentDetails = 'PaymentDetails',
  PlanDetails = 'PlanDetails',
}

export interface PlanCardProps {
  buttonLoading?: boolean;
  planDetails: ProductOffering;
  expandable?: boolean;
  fluid?: boolean;

  onBuyNowClick?: (plan: ProductOffering) => void;

  activePage?: PlanCardPageType;
  isCardLinkable?: boolean;
}

const PlanCard = ({
  buttonLoading,
  planDetails,
  fluid = false,
  expandable = false,

  onBuyNowClick,

  activePage,
  isCardLinkable,
}: PlanCardProps) => {
  const { t } = useTranslation();
  const className = 'c-PlanCard';
  const [isExpanded, setIsExpanded] = useState(!expandable);

  const handleBuyNowClick = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onBuyNowClick?.(planDetails);
  };

  const planType = resolveProductOfferingType(planDetails);

  if (!planDetails) return null;

  const strikethroughOldData = planDetails.old_data;

  return (
    <Collapsible.Root
      asChild
      disabled={true}
      open={isExpanded}
      onOpenChange={expandable ? setIsExpanded : noop}
    >
      <Collapsible.Trigger asChild>
        <article
          className={classNames(className, `${className}--${planType}`, {
            [`${className}--fluid`]: fluid,
            [`${className}--expandable`]: expandable,
          })}
          data-testid={'plan-card'}
          tabIndex={0}
        >
          {strikethroughOldData &&
            activePage !== PlanCardPageType.PaymentDetails && (
              <div className={`${className}__strikethrough-data`}>
                <Body size={16} strikethrough>
                  {t('plan_card.old_data', '{{data}} GB eSIM', {
                    data: strikethroughOldData,
                  })}
                </Body>
              </div>
            )}
          <PlanTitle
            planDetails={planDetails}
            expandable={expandable}
            expanded={isExpanded}
          />
          <Collapsible.Content className={`${className}__collapsible__content`}>
            <div className={`${className}__body`}>
              <div className={`${className}__details-section`}>
                <div
                  className={`${className}__details-tiles-container ${className}__details-tiles-container--${planType}`}
                >
                  <PlanBenefits
                    planDetails={planDetails}
                    showPrice={!expandable}
                  />
                </div>
              </div>
              {(onBuyNowClick || isCardLinkable) && (
                <div className={`${className}__action-section`}>
                  {isCardLinkable && (
                    <div
                      className={classNames([
                        `${className}__spinner-container ${className}__plan_details-btn`,
                        {
                          [`${className}__plan_details-btn--featured`]:
                            planType === 'featured',
                        },
                      ])}
                    >
                      <ButtonV2
                        asChild
                        fluid
                        variant="secondary"
                        size="small"
                        intent="neutral-gray"
                        data-testid="button-plan-details"
                        className={`${className}__plan_details-btn--link`}
                      >
                        <span
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          {t('plan_card.more_info', 'More Info')}
                        </span>
                      </ButtonV2>
                    </div>
                  )}

                  {handleBuyNowClick && (
                    <BuyButton
                      size="small"
                      stockAvailable={isStockAvailable(planDetails)}
                      handleBuyNowClick={handleBuyNowClick}
                      isLoading={buttonLoading}
                    >
                      {t('plan_card.checkout', 'Buy Now')}
                    </BuyButton>
                  )}
                </div>
              )}
              <PlanOperatorFooter planDetails={planDetails} />
            </div>
          </Collapsible.Content>
          {!expandable && planType === 'featured' ? (
            <FeaturedLabel
              text={planDetails.featured_text}
              textFrom={planDetails.featured_text_from}
              textTo={planDetails.featured_text_to}
              featuredStyle={planDetails.featured_style}
            />
          ) : null}
        </article>
      </Collapsible.Trigger>
    </Collapsible.Root>
  );
};

export default PlanCard;
